/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';

import sizes from 'constants/sizes';
import employeesSource from 'constants/employeesSource';

import { useTranslate } from 'hooks/useTranslate';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import { getTotalEmployeesWithIncentive } from 'utils/employees';

import transformData from 'modules/_shared/components/Tables/helpers';
import AddEmployeesToPlan from 'modules/_shared/components/Modals/AddEmployeesToPlan';
import {
  getEmployeesInfo,
  getSocietyEmployees,
  saveEmployees,
} from 'modules/beneficiaries/redux/employeeActions';
import { setModal } from 'modules/_shared/redux/modalActions';
import { getStockPlans } from 'modules/beneficiaries/redux/planActions';
import { parseEmployeesData } from 'modules/beneficiaries/utils/parseEmployeesData';
import { getEmployeesPageDescription } from 'modules/beneficiaries/utils/getEmployeesPageDescription';
import { useGetPlanSectionPermissions } from 'modules/beneficiaries/hooks/useGetPlanSectionPermissions';

import menuTypes from 'constants/menuTypes';
import tableColumns from '../utils/tableColumns';

const isEnabledAndValidated = (integration) =>
  integration.enabled && integration.validated;

export const useEmployeeList = () => {
  const { t, i18n } = useTranslate();
  const dispatch = useDispatch();
  const { societyId } = useParams();

  const { isAdmin, isReadOnly } = useGetPlanSectionPermissions().data;

  const employees = useSelector((state) => state.employees);
  const actualSociety = useSelector((state) => state.society?.actualSociety);
  const plans = useSelector((state) => state.plans);

  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [currentTableColumns, setCurrentTableColumns] = useState(
    tableColumns(i18n)
  );
  const [activeEmployees, setActiveEmployees] = useState([]);
  const [beneficiariesCIFList, setBeneficiariesCIFList] = useState([]);
  const [showInactives, setShowInactives] = useState(false);
  const [kpiFilter, setKpiFilter] = useState('active');

  useGetMenu(menuTypes.EXTENDED);
  useGetActualSociety(societyId);

  const getAllBeneficiariesCIFs = (beneficiaries) => {
    const cifs = beneficiaries?.map((beneficiary) =>
      beneficiary.cif?.toUpperCase()
    );
    setBeneficiariesCIFList(cifs);
  };

  const handleLoadEmployees = async () => {
    try {
      if (isReadOnly) return;

      setIsLoading(true);

      const integrations = {};

      Object.entries(actualSociety?.integrations)?.forEach(([key, value]) => {
        if (isEnabledAndValidated(value) && value.token) {
          integrations[key] = value.token;
        }
      });

      const hasValidKeys = Object.keys(integrations).length > 0;

      if (hasValidKeys) {
        let employeesList = [];

        if (integrations.factorial) {
          employeesList = await getEmployeesInfo(
            employeesSource.FACTORIAL,
            employees,
            actualSociety
          );
        }

        setIsLoading(false);

        if (employeesList.newEmployees || employeesList.updatedEmployees) {
          return Swal.fire({
            icon: 'info',
            title: `<h4 class="nk-modal-title">${t(
              'EmployeesImportModalTitle'
            )}</h4>`,
            html: `<p class="text-soft px-4" style="margin-top:24px;margin-bottom:30px">
            ${t('EmployeesImportModalDescription', {
              newEmployees: employeesList.newEmployees?.length,
              updatedEmployees: employeesList.updatedEmployees?.length,
            })}
          </p>`,
            confirmButtonText: t('EmployeesImportModalConfirmButtonText'),
            confirmButtonColor: '#6576ff',
            allowOutsideClick: false,
            showCancelButton: true,
          }).then((result) => {
            if (result.isConfirmed) {
              dispatch(
                saveEmployees(
                  employeesList,
                  employeesSource.FACTORIAL,
                  actualSociety['_id']
                )
              );
            }
          });
        }
      } else {
        return Swal.fire({
          icon: 'warning',
          title: `<h4 class="nk-modal-title">${t('HasNotValidKeys')}<br></h4>`,
          html: `<p class="text-soft px-4">${t(
            'HasNotValidKeysDescription'
          )}</p>
        <p class="text-primary" style="margin-top:24px;margin-bottom:30px">
          ${t('IntegrationsTabAndPage')}
        </p>`,
          confirmButtonText: t('OK'),
          allowOutsideClick: false,
          showCancelButton: false,
          confirmButtonColor: '#6576FF',
        });
      }
    } catch (error) {
      console.error('handleLoadEmployees', error); // eslint-disable-line no-console
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterEmployees = () => {
    if (isReadOnly) return;
    if (plans?.length) {
      dispatch(setModal(<AddEmployeesToPlan size={sizes.LG} />));
    } else {
      return Swal.fire({
        icon: 'warning',
        title: `<h4 class="nk-modal-title">${t('HasNoPlans')}</h4>`,
        html: `<p class="text-soft px-4">${t('HasNoPlansDescription')}</p>
        <p class="text-primary" style="margin-top:24px;margin-bottom:30px">
          ${t('PlansListPage')}
        </p>`,
        confirmButtonText: t('OK'),
        allowOutsideClick: false,
        showCancelButton: false,
        confirmButtonColor: '#6576FF',
      });
    }
  };

  useEffect(() => {
    dispatch(getStockPlans(societyId));
    dispatch(getSocietyEmployees(societyId));
  }, [societyId]);

  useEffect(() => {
    if (actualSociety) {
      getAllBeneficiariesCIFs(actualSociety?.beneficiaries);
    }
  }, [actualSociety]);

  useEffect(() => {
    const newData = parseEmployeesData({
      employees,
      beneficiariesCIF: beneficiariesCIFList,
      society: actualSociety,
      t,
    });

    if (newData?.length > 0) {
      setTableData(
        transformData({
          data: newData,
          columns: tableColumns(i18n),
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees]);

  useEffect(() => {
    setCurrentTableColumns(tableColumns(i18n));
  }, [i18n.language]);

  useEffect(() => {
    if (employees?.length) {
      setActiveEmployees(
        employees.filter((employee) => !employee.terminationDate)
      );
    } else {
      setActiveEmployees([]);
    }
  }, [employees]);

  const pageDescription = getEmployeesPageDescription({
    society: actualSociety,
    employees,
    t,
  });

  const activeEmployeesWithIncentive = getTotalEmployeesWithIncentive(
    activeEmployees,
    beneficiariesCIFList
  );

  const inactiveEmployeesWithIncentive = getTotalEmployeesWithIncentive(
    employees?.filter((employee) => employee.terminationDate) || [],
    beneficiariesCIFList
  );

  const getFilteredTableData = () => {
    if (kpiFilter === 'active') {
      return tableData.filter((row) => row.isActive === true);
    }
    if (kpiFilter === 'activeWithIncentives') {
      return tableData.filter(
        (row) => row.isActive === true && row.hasIncentivePlan === true
      );
    }
    if (kpiFilter === 'inactiveWithIncentives') {
      return tableData.filter(
        (row) => row.isActive === false && row.hasIncentivePlan === true
      );
    }
    return showInactives
      ? tableData
      : tableData.filter((row) => row.isActive === true);
  };

  const filteredTableData = getFilteredTableData();

  const handleToggleShowInactives = () => {
    setShowInactives((prev) => !prev);
  };

  const handleKpiFilterClick = (filter) => {
    setKpiFilter((prev) => (prev === filter ? null : filter));
  };

  return {
    data: {
      actualSociety,
      tableData: filteredTableData,
      currentTableColumns,
      showInactives,
      kpiFilter,
      activeEmployees,
      employees,
      isAdmin,
      isReadOnly,
      pageDescription,
      activeEmployeesWithIncentive,
      inactiveEmployeesWithIncentive,
    },
    actions: {
      handleLoadEmployees,
      handleFilterEmployees,
      handleToggleShowInactives,
      handleKpiFilterClick,
    },
    isLoading,
    isError: false,
  };
};
